<template>
  <cs-form-dialog
    v-model="popupDialog"
    :heading="`${isUpdate ? 'Update' : 'New'} Link Properties`"
    :primary-action="{ label: 'Save', eventName: 'dlg-ok', disabled: false }"
    :secondary-action1="{ label: 'Remove Link', eventName: 'dlg-remove', delete: true }"
    @primary-click="tipTapLinkDialogSave"
    @secondary1-click="tipTapLinkDialogRemoveLink"
  >
    <template #cs-form-dialog-content>
      <v-form
        ref="linkEditorForm"
        v-model="linkEditorFormIsValid"
        @keyup.enter="tipTapLinkDialogSave"
        @submit.prevent.stop="tipTapLinkDialogSave"
      >
        <v-text-field
          v-if="!isUpdate"
          v-model="linkText"
          prepend-icon="mdi-text-shadow"
          label="Text to Display"
          single-line="single-line"
          :disabled="!isSelectionEmpty"
          :rules="[inputValidation.textToDisplayIsRequired]"
          @keydown.esc="tipTapLinkDialogCancel"
        ></v-text-field>

        <v-text-field
          v-model="linkUrl"
          prepend-icon="mdi-link"
          label="URL"
          single-line="single-line"
          :rules="[inputValidation.linkUrlIsValid]"
          @keydown.esc="tipTapLinkDialogCancel"
          @paste="onLinkUrlPaste"
          @update:model-value="onLinkUrlInput"
        ></v-text-field>

        <v-label v-if="linkUrlIsInternalLink && isUsingPastedLink">
          Why is my link converting to ce-viewer.com?
          <v-tooltip location="right">
            <template #activator="{ props }">
              <v-icon v-bind="props" class="mx-2" size="small">mdi-information</v-icon>
            </template>
            <div class="linkUrlIsInternalLinkTooltip">
              {{
                isGroupSchoolParent
                  ? 'Your link converts to ce-viewer.com for faster page loading on the PolicyConnect site and also allows child sites to display shared content on their own site. You can unselect the checkbox below it if you want child sites to access specific pages on the parent site, for example via a public link when content differs.'
                  : 'Your link converts to ce-viewer.com for faster page loading on your PolicyConnect site. This improves your browsing experience by reducing load times and is set up for your convenience. If you want the link to behave differently, simply untick the checkbox below.'
              }}
            </div>
          </v-tooltip>
        </v-label>

        <v-checkbox
          v-if="linkUrlIsInternalLink"
          v-model="isUsingCeViewerLink"
          class="mr-4"
          :label="
            isGroupSchoolParent
              ? 'Enable child sites to access links internally'
              : 'Open internal links in same tab'
          "
        />
      </v-form>
    </template>
  </cs-form-dialog>
</template>

<script>
import { mapState } from 'vuex';
import { CSBase, CSFormDialog } from '@complispace/cs-design-system';
import markdownIt from 'markdown-it';
import { markdownItFancyListPlugin } from 'markdown-it-fancy-lists';
import routerUtils from '@/lib/routerUtils';
import sessionManager from '@/store/modules/session/sessionManager';

const md = markdownIt({
  html: true
});

md.use(markdownItFancyListPlugin);

export default {
  name: 'TipTapLinkEditorDialog',

  components: {
    CSFormDialog
  },
  extends: CSBase,

  props: {
    showDialog: { type: Boolean, required: true, default: false },
    getEditor: { type: Function, required: true }
  },

  emits: ['hideLinkEditorDialog', 'removeLinkMarkFromSelection'],

  data() {
    return {
      // <a> properties
      shouldOpenInNewTab: false,
      linkUrl: '',
      linkText: '',
      isUsingPastedLink: false,

      // dialog properties
      popupDialog: false,
      isUpdate: false,
      isUsingCeViewerLink: false,
      isSelectionEmpty: false,
      linkUrlIsInternalLink: false,
      isGroupSchoolParent: false,

      // form properties
      linkEditorFormIsValid: false,
      inputValidation: {
        textToDisplayIsRequired: (value) => {
          if (this.isUpdate || !this.isSelectionEmpty) return true;
          return !!value || 'This field is required.';
        },
        linkUrlIsValid: (value) => {
          const pattern = /^(mailto:\s?|https?:\/\/)[^ "]+$/;
          return (
            pattern.test(value) ||
            'Please enter a valid link url, for example https://ce-viewer.com'
          );
        }
      }
    };
  },
  computed: {
    ...mapState({
      client: (state) => state.client
    })
  },

  watch: {
    showDialog(value) {
      if (value) {
        this.popupDialog = true;
        this.initializeState();
      } else {
        this.popupDialog = false;
      }
    },
    popupDialog(value) {
      if (!value) {
        this.$emit('hideLinkEditorDialog');
      }
      this.$nextTick(() => {
        this.resetFormValidation();
      });
    },
    linkUrl(value) {
      const { isCeViewerLink, isInternalLink } = this.checkLinkAndHostname(value);
      if (isCeViewerLink) {
        this.linkUrlIsInternalLink = isInternalLink;
        this.isUsingCeViewerLink = true;
      } else {
        this.linkUrlIsInternalLink = isInternalLink;
        this.isUsingCeViewerLink = false;
      }
      this.shouldOpenInNewTab = !isInternalLink;
    },
    isUsingCeViewerLink(value) {
      if (value) {
        this.convertToCeViewerLink();
      } else {
        this.convertToPreviousLink();
      }
    }
  },

  methods: {
    /**
     * Initializes the state of the link editor dialog whenever the dialog is opened.
     * @method
     * @returns {void}
     */
    initializeState() {
      this.isSelectionEmpty = this.checkIsSelectionEmpty();
      const { href, text, isUpdate } = this.getHighlightedLinkProperties();
      this.isGroupSchoolParent = this.checkClientIsGroupSchoolParent();
      this.linkUrl = href;
      this.linkText = text;
      const { isInternalLink } = this.checkLinkAndHostname(href);
      this.shouldOpenInNewTab = !isInternalLink;
      this.isUpdate = isUpdate;
    },
    /**
     * Checks the link URL and hostname information.
     * @method
     * @param {string} url - The URL to check.
     * @returns {LinkAndHostnameInfo} - Information about the link URL and hostname.
     */
    checkLinkAndHostname(url) {
      const { currentWindowUrl, isCeViewerLink, isInternalLink } =
        routerUtils.analyzeUrlForNavigation(url);

      return {
        isInternalLink,
        isCeViewerLink,
        currentWindowUrl
      };
    },

    /**
     * Converts the link URL to the ce-viewer format.
     * @method
     * @returns {void}
     */
    convertToCeViewerLink() {
      // normal link: http://schoola.complispaceprimary.localhost:9183/module/3139/page/eeb5702f-130a-4f84-9a5c-d0d51248c914.md
      // ce-viewer link: http://ce-viewer.com/module/3139/page/eeb5702f-130a-4f84-9a5c-d0d51248c914.md
      const { isInternalLink, isCeViewerLink } = this.checkLinkAndHostname(this.linkUrl);
      if (!isInternalLink || isCeViewerLink) return;

      const linkUrlInstance = new URL(this.linkUrl);
      const { pathname } = linkUrlInstance;

      const newCeViewerUrl = `https://ce-viewer.com${pathname}`;
      this.linkUrl = newCeViewerUrl;
    },
    /**
     * Converts the ce-viewer link URL back to the previous format with current hostname.
     * @method
     * @returns {void}
     */
    convertToPreviousLink() {
      const { isCeViewerLink } = this.checkLinkAndHostname(this.linkUrl);
      if (!isCeViewerLink) return; // nothing to convert

      const linkUrlInstance = new URL(this.linkUrl);
      const { pathname } = linkUrlInstance;
      const newInternalLink = `${sessionManager.getXSiteUrl()}${pathname}`;
      this.linkUrl = newInternalLink;
    },
    /**
     * Retrieves the text content of the current text selection in the editor.
     * @method
     * @returns {string} - The text content of the selection.
     */
    getSelectionTextFromEditorState() {
      const editor = this.getEditor();
      const selectionSlice = editor?.view?.state?.selection?.content?.();
      const selectionNodes = selectionSlice?.content?.content ?? [];
      const selectionText = selectionNodes
        .map((node) => {
          if (node.textContent) return node.textContent;

          const textNodes = node?.content?.content ?? [];
          return textNodes.map((textNode) => textNode?.text).join(' ');
        })
        .join(' ');

      return selectionText;
    },
    /**
     * Retrieves the properties of a highlighted link in the editor.
     * @method
     * @returns {LinkProperties} - The properties of the highlighted link.
     */
    getHighlightedLinkProperties() {
      const editorState = this.getEditor()?.state;
      if (!editorState) return {};

      let target = '_self';
      let text = '';
      let href = '';

      const isUpdate = this.checkCursorIsBetweenLink() || this.checkSelectionIsLink();
      if (isUpdate) {
        // update existing link
        const { from, to } = editorState.selection; // get marks, if any from selected area
        let targetedNodes = [];
        let targetedMarks = [];
        editorState.doc?.nodesBetween(from, to, (node) => {
          const eligibleMark = node.marks?.find((markItem) => markItem?.type?.name === 'link');
          if (eligibleMark) {
            targetedNodes = [...targetedNodes, node];
            targetedMarks = [...targetedMarks, eligibleMark];
          } else if (node.text) {
            targetedNodes = [...targetedNodes, node];
          }
        });
        if (targetedMarks.length >= 1) {
          href = targetedMarks?.[0]?.attrs?.href || '';
          target = targetedMarks[0]?.attrs.target || '_self';
          text = targetedNodes.map((node) => node.text).join(' ');
        } else if (targetedNodes.length >= 1) {
          // highlighted section consists of text only
          text = targetedNodes.map((node) => node.text).join(' ');
        }
      } else {
        // create new link
        const currentSelectionIsEmpty = this.isSelectionEmpty; // do not execute logic below if selection is empty
        text = currentSelectionIsEmpty ? '' : this.getSelectionTextFromEditorState();
      }

      return { href, target, text, isUpdate };
    },

    /**
     * Checks if the text cursor is in between a link element.
     * @method
     * @returns {boolean} - `true` if the cursor is at a link element, otherwise `false`.
     */
    checkCursorIsBetweenLink() {
      const editor = this.getEditor();
      if (!editor) return false;
      const {
        state: { tr }
      } = editor;

      const [link] = tr?.selection?.$anchor?.marks?.() ?? [];
      const cursorIsAtLinkElement = link?.type?.name === 'link';

      return cursorIsAtLinkElement;
    },
    /**
     * Checks if the client is a group school parent.
     * @method
     * @returns {boolean} - `true` if the client is a group school parent, otherwise `false`.
     */
    checkClientIsGroupSchoolParent() {
      const { client } = this;
      const isGroupSchoolParent = client.children && client.children.length > 0;
      return isGroupSchoolParent;
    },
    /**
     * Checks if the current text selection in the editor is empty.
     * @method
     * @returns {boolean} - `true` if the selection is empty, otherwise `false`.
     */
    checkIsSelectionEmpty() {
      const editor = this.getEditor();
      if (!editor) return true;
      const selection = editor?.view?.state?.tr?.selection;
      return !!selection?.empty; // selection is empty
    },
    /**
     * Appends a new link to the editor state.
     * @method
     * @param {string} linkText - The text of the link.
     * @param {string} newLinkUrl - The URL of the new link.
     * @returns {void}
     */
    appendNewLinkToEditorState(linkText, newLinkUrl) {
      const editorInstance = this.getEditor();
      if (!editorInstance) return;

      editorInstance
        .chain()
        .focus()
        .extendMarkRange('link')
        .command(({ tr, dispatch }) => {
          if (!dispatch) return true;

          const { from, to } = tr.selection;

          tr.insertText(linkText, from, to);
          tr.addMark(
            from,
            from + linkText.length,
            editorInstance.schema.marks.link.create({ href: newLinkUrl })
          );

          return true;
        })
        .run();
    },
    /**
     * Checks if the current text selection in the editor is a link.
     * @method
     * @returns {boolean} - `true` if the selection is a link, otherwise `false`.
     */
    checkSelectionIsLink() {
      const editor = this.getEditor();
      if (!editor) return false;

      const { from, to } = editor?.state?.selection || {};
      let selectionHasLinkInBetween = false;
      editor?.state?.doc?.nodesBetween(from, to, (node) => {
        const eligibleMark = node.marks?.find((markItem) => markItem?.type?.name === 'link');
        if (eligibleMark) {
          selectionHasLinkInBetween = true;
        }
      });
      return selectionHasLinkInBetween;
    },

    /**
     * Handles setting the link URL and updating the editor state.
     *
     * REFERENCE: https://github.com/ueberdosis/tiptap/issues/490
     * @method
     * @returns {void}
     */
    handleSetLinkUrl() {
      const newLinkUrl = this.linkUrl;

      const currentSelectionIsEmpty = this.isSelectionEmpty; // check if user has highlighted text before clicking the link button

      if (currentSelectionIsEmpty) {
        // empty selection
        const selectionIsLink = this.checkSelectionIsLink(); // check if text cursor is in between a link
        // user has not highlighted text before clicking the link button
        if (newLinkUrl) {
          // user has entered a new link url
          this.appendNewLinkToEditorState(this.linkText, newLinkUrl);
        } else if (!newLinkUrl && selectionIsLink) {
          // 'CASE: Remove link, when submit a empty url'
          this.$emit('removeLinkMarkFromSelection');
        }
      } else {
        // 'CASE: Select a text and create/update a link (common way)'
        const editorInstance = this.getEditor();
        editorInstance
          ?.chain()
          .focus()
          .extendMarkRange('link')
          .setLink({ href: newLinkUrl, target: this.shouldOpenInNewTab ? '_blank' : '_self' })
          .run();
      }
    },
    /**
     * Handles the paste event for link URL. Change the link URL to ce-viewer link if the pasted text is a valid URL.
     * @method
     * @param {ClipboardEvent} event - The paste event.
     * @returns {void}
     */
    onLinkUrlPaste(event) {
      const pasteText = event.clipboardData.getData('text');
      const { isInternalLink } = this.checkLinkAndHostname(pasteText);
      if (isInternalLink) {
        event.preventDefault();
        event.stopPropagation();
        this.isUsingPastedLink = true;
        this.linkUrl = pasteText;
        this.shouldOpenInNewTab = !isInternalLink;
        this.convertToCeViewerLink();
      }
    },
    /**
     * Handles the input event for link URL.
     *
     * @method
     * @returns {void}
     */
    onLinkUrlInput() {
      this.isUsingPastedLink = false;
    },
    /**
     * Resets the form validation for the link editor dialog.
     *
     * @method
     * @returns {void}
     */
    resetFormValidation() {
      this.$refs.linkEditorForm.resetValidation();
    },
    /**
     * Runs the form validation for the link editor dialog.
     *
     * @method
     * @returns {void}
     */
    runFormValidation() {
      this.$refs.linkEditorForm.validate();
    },
    /**
     * Saves the changes made in the link editor dialog and closes the dialog.
     * @method
     * @returns {void}
     */
    tipTapLinkDialogSave() {
      this.runFormValidation();
      if (!this.linkEditorFormIsValid) return;
      this.handleSetLinkUrl();
      this.popupDialog = false;
    },
    /**
     * Cancels and closes the link editor dialog.
     * @method
     * @returns {void}
     */
    tipTapLinkDialogCancel() {
      this.popupDialog = false;
    },
    /**
     * Removes the link mark from the current selection (remove only highlighted portion) and closes the dialog.
     * @method
     * @returns {void}
     */
    tipTapLinkDialogRemoveLink() {
      this.$emit('removeLinkMarkFromSelection');
      this.popupDialog = false;
    }
  }
};
</script>

<style scoped>
.linkUrlIsInternalLinkTooltip {
  font-size: medium;
  max-width: 350px;
  white-space: normal;
  text-align: justify;
}
</style>
